import * as angular from 'angular';
import * as _ from 'underscore';
import { LanguageService } from '../../../../app.common/modules/common/services/languge.service';
import { AccountService } from '../../account/account.service';
import { IOfferType, ISpecialOffer, OfferTemplateTypeEnum, SpecialOfferTypeEnum } from '../models';

export class OffersService {
    public static $inject = [
        '$translate',
        'accountService',
        'languageService'
    ];

    constructor(
        private $translate: angular.translate.ITranslateService,
        private accountService: AccountService,
        private languageService: LanguageService
    ) {}

    public getOfferTypes(): IOfferType[] {
        return this.generateOfferTypes();
    }

    public isValidOffer(offer: ISpecialOffer, price: number | string): boolean {
        offer.error = {};
        const offerTypes = this.generateOfferTypes();
        const offerType = offerTypes.find((type) => type.value === offer.Type);
        return offerType.validators.reduce((prevValue, func) => {
            return func(offer, price) && prevValue;
        }, true);
    }

    private checkIfDatesCorrect(offer: ISpecialOffer): boolean {
        if (!offer.OfferValidFrom) {
            offer.error.startDate = this.$translate.instant('field_is_required');
            return false;
        }
        if (!offer.OfferValidFrom) {
            offer.error.startDate = this.$translate.instant('field_is_required');
            return false;
        }
        if (offer.OfferValidFrom >= offer.OfferValidTo) {
            offer.error.endDate = this.$translate.instant('invalid_end_date');
            return false;
        }
        return true;
    }

    private checkIfDiscountCorrect(offer: ISpecialOffer, oldPrice: number | string) {
        const discount = parseFloat(offer.DiscountPrice as string);
        const price = parseFloat(oldPrice as string);
        if (_.isNaN(discount) || discount <= 0) {
            offer.error.price = this.$translate.instant('must_be_greater_than_0');
            return false;
        }
        if (!_.isNaN(price) && price <= discount) {
            offer.error.price = this.$translate.instant('must_be_less_than_the_old_price');
            return false;
        }
        const diff = Number.parseFloat((price - discount).toFixed(2));
        const countryID = this.accountService.userData.countryID;
        if (offer.TemplateType === OfferTemplateTypeEnum.SaveXX && diff < 1 && diff > 0) {
            offer.error.price = this.$translate.instant('not_pound_offer', {countryID});
            return false;
        }
        if (offer.TemplateType === OfferTemplateTypeEnum.SaveXXp && diff > 0.99) {
            offer.error.price = this.$translate.instant('not_pence_offer', {countryID});
            return false;
        }
        if ((offer.TemplateType === OfferTemplateTypeEnum.NowOnlyXX || offer.TemplateType === OfferTemplateTypeEnum.OnlyXX) && offer.DiscountPrice < 1) {
            offer.error.price = this.$translate.instant('price_must_be_not_less_pound', {countryID});
            return false;
        }
        if (offer.TemplateType === OfferTemplateTypeEnum.NowOnlyXXp && offer.DiscountPrice >= 1) {
            offer.error.price = this.$translate.instant('price_must_be_less_pound', {countryID});
            return false;
        }
        return true;
    }

    private checkIfBuyXGetYCorrect(offer: ISpecialOffer): boolean {
        let result = true;
        if (!offer.BuyXGetYOfferValues.Buy || offer.BuyXGetYOfferValues.Buy < 1) {
            offer.error.buyQuantity = this.$translate.instant('must_be_greater_than_0');
            result = false;
        }
        if (!offer.BuyXGetYOfferValues.Get || offer.BuyXGetYOfferValues.Get < 1) {
            offer.error.getQuantity = this.$translate.instant('must_be_greater_than_0');
            result = false;
        }
        return result;
    }

    private checkIfBuyYForYCorrect(offer: ISpecialOffer): boolean {
        let result = true;
        const countryID = this.accountService.userData.countryID;
        if (!offer.BuyXForYSumYOfferValues.Buy || offer.BuyXForYSumYOfferValues.Buy < 1) {
            offer.error.buyQuantity = this.$translate.instant('must_be_greater_than_0');
            result = false;
        }
        const sum = parseFloat(offer.BuyXForYSumYOfferValues.Sum as string);
        if (_.isNaN(sum) || sum <= 0) {
            offer.error.price = this.$translate.instant('must_be_greater_than_0');
            result = false;
        }
        if (offer.TemplateType === OfferTemplateTypeEnum.BuyXForY && offer.BuyXForYSumYOfferValues.Sum < 1) {
            offer.error.price = this.$translate.instant('price_must_be_not_less_pound', {countryID});
            result = false;
        }
        if (offer.TemplateType === OfferTemplateTypeEnum.BuyXForYp && offer.BuyXForYSumYOfferValues.Sum >= 1) {
            offer.error.price = this.$translate.instant('price_must_be_less_pound', {countryID});
            result = false;
        }
        return result;
    }

    private generateOfferTypes(): IOfferType[] {
        const settings = this.languageService.getLangSettingsOrDefault(this.accountService.userData.countryID);
        const wholeSign = settings.currencySign + settings.currencySeparator;
        const fracSign = settings.currencySeparator + settings.currencyFracSign;
        return [
            {
                name: this.$translate.instant('discount'),
                value: SpecialOfferTypeEnum.Discount,
                validators: [
                    this.checkIfDatesCorrect.bind(this),
                    this.checkIfDiscountCorrect.bind(this)
                ],
                templates: [
                    {
                        name: this.$translate.instant('template_type_save_xx', {sign: wholeSign}),
                        value: OfferTemplateTypeEnum.SaveXX
                    },
                    {
                        name: this.$translate.instant('template_type_save_xx_p', {sign: fracSign}),
                        value: OfferTemplateTypeEnum.SaveXXp
                    },
                    {
                        name: this.$translate.instant('template_type_now_only_xx', {sign: wholeSign}),
                        value: OfferTemplateTypeEnum.NowOnlyXX
                    },
                    {
                        name: this.$translate.instant('template_type_now_only_xx_p', {sign: fracSign}),
                        value: OfferTemplateTypeEnum.NowOnlyXXp
                    },
                    {
                        name: this.$translate.instant('template_type_only_xx', {sign: wholeSign}),
                        value: OfferTemplateTypeEnum.OnlyXX
                    }
                ]
            },
            {
                name: this.$translate.instant('buy_x_get_y'),
                value: SpecialOfferTypeEnum.BuyXGetY,
                validators: [
                    this.checkIfDatesCorrect.bind(this),
                    this.checkIfBuyXGetYCorrect.bind(this)
                ]
            },
            {
                name: this.$translate.instant('buy_x_for_y'),
                value: SpecialOfferTypeEnum.BuyXForY,
                validators: [
                    this.checkIfDatesCorrect.bind(this),
                    this.checkIfBuyYForYCorrect.bind(this)
                ],
                templates: [
                    {
                        name: this.$translate.instant('template_type_buy_x_for_y', {sign: wholeSign}),
                        value: OfferTemplateTypeEnum.BuyXForY
                    },
                    {
                        name: this.$translate.instant('template_type_buy_x_for_y_p', {sign: fracSign}),
                        value: OfferTemplateTypeEnum.BuyXForYp
                    }
                ]
            }
        ];
    }

}

angular
    .module('naut')
    .service('offersService', OffersService);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/products/services/offers.service.ts